import { ChangeEvent, useId } from "react";
import { formatErrorMessageV2 } from "../utils";

export type InputV2Props = {
  id?: string;
  name: string;
  label?: string;
  description?: string;
  value?: string | number | null;
  type?: "text" | "number" | "tel" | "textarea" | "hidden";
  mask?: "phone" | "cpfCnpj";
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  prefix?: string;
  suffix?: string;
  anchor?: "start" | "center" | "end";
  maxLength?: number;
  min?: number;
  step?: number | "any";
  onChange?: (value: string, event?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: () => void;
};

export function InputV2({
  id,
  name,
  label,
  description = "",
  value,
  type = "text",
  mask,
  placeholder,
  disabled = false,
  error,
  prefix,
  suffix,
  anchor = "start",
  maxLength,
  min,
  step,
  onChange,
  onBlur,
}: InputV2Props) {
  const generatedId = useId().replace(/:/g, "");
  const inputId = id || `onboarding-v2-${name.replace(/[^a-zA-Z0-9_-]/g, "-")}-${generatedId}`;
  const inputValue = value ?? "";
  const visibleValue = formatMaskedValue(String(inputValue), mask);
  const suffixInputWidth = suffix
    ? `${Math.max(String(visibleValue || placeholder || "").replace(".", "").length, 1)}ch`
    : undefined;
  const inputMaxLength = mask === "phone" ? 15 : mask === "cpfCnpj" ? 18 : maxLength;

  if (type === "hidden") {
    return <input type="hidden" name={name} value={inputValue} />;
  }

  return (
    <label className="ov2-field" htmlFor={inputId}>
      {label && <span className="ov2-field__label">{label}</span>}
      {description && <span className="ov2-field__description">{description}</span>}
      <span
        className={`ov2-input ov2-input--anchor-${anchor} ${suffix ? "ov2-input--with-suffix" : ""} ${error ? "ov2-input--error" : ""} ${
          disabled ? "ov2-input--disabled" : ""
        }`}
      >
        {prefix && <span className="ov2-input__affix">{prefix}</span>}
        {type === "textarea" ? (
          <textarea
            id={inputId}
            name={name}
            value={String(inputValue)}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
            onBlur={onBlur}
            onChange={(event) => onChange && onChange(event.target.value, event)}
          />
        ) : (
          <input
            id={inputId}
            name={name}
            value={visibleValue}
            type={type}
            min={min}
            step={step}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={inputMaxLength}
            autoComplete="off"
            style={suffixInputWidth ? { width: suffixInputWidth } : undefined}
            onBlur={onBlur}
            onChange={(event) => onChange && onChange(getMaskedChangeValue(event.target.value, mask), event)}
          />
        )}
        {suffix && <span className="ov2-input__affix">{suffix}</span>}
      </span>
      {error && <small className="ov2-field__error animation__fadeIn">{formatErrorMessageV2(error)}</small>}
    </label>
  );
}

function onlyDigits(value: string) {
  return value.replace(/\D/g, "");
}

function getMaskedChangeValue(value: string, mask?: InputV2Props["mask"]) {
  if (mask === "phone") return onlyDigits(value).slice(0, 11);
  if (mask === "cpfCnpj") return onlyDigits(value).slice(0, 14);
  return value;
}

function formatMaskedValue(value: string, mask?: InputV2Props["mask"]) {
  if (mask === "phone") return formatPhone(value);
  if (mask === "cpfCnpj") return formatCpfCnpj(value);
  return value;
}

export function formatPhone(value: string) {
  const digits = onlyDigits(value).slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

function formatCpfCnpj(value: string) {
  const digits = onlyDigits(value).slice(0, 14);

  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  if (digits.length <= 11) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}
